import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/Sidebar';
import Lockers from './pages/Lockers';
import Controllers from './pages/Controllers';
import Readers from './pages/Readers';
import Groups from './pages/Groups';
import Users from './pages/Users';
import Analytics from './pages/Analytics';
import Heartbeat from './pages/Heartbeat';
import Settings from './pages/Settings';
import QuickRegister from './pages/QuickRegister';
import Login from './pages/Login';
import Unlock from './pages/Unlock';
import Kiosk from './pages/Kiosk';
import { SocketProvider, useSocket } from './contexts/SocketContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LockerDrawerProvider } from './contexts/LockerDrawerContext';
import CommandPalette from './components/CommandPalette';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';

// Authenticated dashboard layout (sidebar + MQTT banner). Child pages render
// into <Outlet/>. Redirects to /login when there is no staff token.
function DashboardLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { offlineAlerts } = useSocket();
  const [mqttStatus, setMqttStatus] = useState('connected');

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let isMounted = true;
    const fetchStatus = async () => {
      try {
        const res = await axios.get('/api/status');
        if (isMounted) setMqttStatus(res.data.mqtt);
      } catch (e) {
        if (isMounted) setMqttStatus('disconnected');
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // poll every 10s
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <LockerDrawerProvider>
    <div className="flex h-screen">
      <Sidebar />
      <CommandPalette />
      <div className="flex-1 flex flex-col overflow-hidden">
        {mqttStatus === 'disconnected' && (
          <div className="material-thick border-b border-danger-500/40 text-danger-300 text-center py-2.5 text-subheadline font-semibold z-50 flex items-center justify-center space-x-2">
            <AlertTriangle className="w-4 h-4" />
            <span>MQTT connection lost. Locker status updates are temporarily unavailable.</span>
          </div>
        )}
        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Offline Alerts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {offlineAlerts.map((alert) => (
          <div
            key={alert.id}
            className="material-thick rounded-2xl p-4 shadow-sheet max-w-sm animate-fade-up"
          >
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-danger-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-subheadline font-semibold text-white">Locker Offline Alert</p>
                <p className="text-subheadline text-secondary mt-1">{alert.message}</p>
                <p className="text-caption text-muted mt-2">
                  {alert.timestamp.toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    </LockerDrawerProvider>
  );
}

// Wraps the authenticated layout in the Socket provider so realtime data is
// only requested for signed-in staff.
function ProtectedArea() {
  return (
    <SocketProvider>
      <DashboardLayout />
    </SocketProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/unlock" element={<Unlock />} />
          {/* Self-service kiosk (locked-down kiosk role, own sign-in) */}
          <Route path="/kiosk" element={<Kiosk />} />

          {/* Authenticated dashboard (layout route with nested pages) */}
          <Route element={<ProtectedArea />}>
            <Route path="/" element={<Navigate to="/register" replace />} />
            <Route path="/register" element={<QuickRegister />} />
            <Route path="/lockers" element={<Lockers />} />
            <Route path="/controllers" element={<Controllers />} />
            <Route path="/readers" element={<Readers />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/users" element={<Users />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/heartbeat" element={<Heartbeat />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/register" replace />} />
          </Route>
        </Routes>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'rgba(28, 28, 36, 0.85)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 255, 255, 0.10)',
              borderRadius: '16px',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 500,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
            },
            success: { iconTheme: { primary: '#30d158', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ff453a', secondary: '#fff' } },
          }}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
